import React, { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import AWS from 'aws-sdk';
import Layout from '../components/Layout';
import '../styles/nlp.css';
import '../styles/service-fix.css'; // Fix for dark text on dark background

const NLP = ({ user }) => {
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    useCase: '',
    nlpType: 'sentiment'
  });
  const [submitStatus, setSubmitStatus] = useState('');
  
  const isAuthenticated = user !== null;

  if (!isAuthenticated) {
    return (
      <Layout>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h2>Authentication Required</h2>
          <p>Please sign in to access NLP services.</p>
        </div>
      </Layout>
    );
  }

  const handleContactInfoChange = (field, value) => {
    setContactInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactInfo.name || !contactInfo.email || !contactInfo.useCase) {
      setSubmitStatus('Please provide your name, email address, and use case.');
      return;
    }
    
    setSubmitStatus('Submitting...');
    
    try {
      // Get AWS credentials from Amplify Auth
      const session = await fetchAuthSession();
      const credentials = session.credentials;
      
      // Configure AWS SDK with proper credentials
      AWS.config.update({
        region: 'us-east-1',
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken
        }
      });
      
      // Send email notification
      const ses = new AWS.SES();
      const emailParams = {
        Source: 'info@relufox.ai',
        Destination: {
          ToAddresses: ['info@relufox.ai']
        },
        Message: {
          Subject: {
            Data: 'New NLP Inquiry - Fox River AI'
          },
          Body: {
            Text: {
              Data: `A new inquiry has been submitted for NLP services:\n\nCONTACT INFORMATION:\nName: ${contactInfo.name}\nEmail: ${contactInfo.email}\nCompany: ${contactInfo.company || 'Not provided'}\nPhone: ${contactInfo.phone || 'Not provided'}\n\nNLP TYPE: ${contactInfo.nlpType}\n\nUSE CASE:\n${contactInfo.useCase}\n\nPlease follow up with this customer.`
            }
          }
        }
      };
      
      await ses.sendEmail(emailParams).promise();
      
      setSubmitStatus('Thank you! Your inquiry has been submitted. We will contact you within 24 hours.');
      setContactInfo({
        name: '',
        email: '',
        company: '',
        phone: '',
        useCase: '',
        nlpType: 'sentiment'
      });
      
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus(`Submission failed: ${error.message || 'Unknown error'}. Please try again or contact us directly.`);
    }
  };

  return (
    <Layout>
      <div className="nlp-page">
        <div className="hero-section">
          <h1>Natural Language Processing (NLP)</h1>
          <p className="hero-subtitle">Extract meaning, structure, and insight from unstructured text data</p>
        </div>

        <div className="content-container">
          <section className="service-overview">
            <h2>Understand Text Like Never Before</h2>
            <p className="section-intro">Our Natural Language Processing (NLP) solutions help businesses extract meaning, structure, and insight from unstructured text — whether that's customer feedback, internal documents, emails, or reviews.</p>
            
            <div className="services-grid">
              <div className="service-item">
                <h3>Sentiment Analysis</h3>
                <p>Determine the emotional tone behind text data</p>
              </div>
              <div className="service-item">
                <h3>Text Classification</h3>
                <p>Automatically categorize documents by topic or intent</p>
              </div>
              <div className="service-item">
                <h3>Named Entity Recognition</h3>
                <p>Identify and extract key entities like people, places, and organizations</p>
              </div>
              <div className="service-item">
                <h3>Text Summarization</h3>
                <p>Generate concise summaries of longer documents</p>
              </div>
            </div>
          </section>

          <section className="technology-section">
            <h2>Advanced NLP Techniques</h2>
            <div className="tech-features">
              <div className="tech-item">
                <h4>🔄 Transformer Models</h4>
                <p>State-of-the-art neural networks for language understanding</p>
              </div>
              <div className="tech-item">
                <h4>📊 Vector Embeddings</h4>
                <p>Sophisticated representations of text for semantic search and analysis</p>
              </div>
              <div className="tech-item">
                <h4>🧠 Fine-Tuned Models</h4>
                <p>Custom-trained models for your specific domain and terminology</p>
              </div>
              <div className="tech-item">
                <h4>⚙️ Processing Pipelines</h4>
                <p>End-to-end workflows for text extraction, cleaning, and analysis</p>
              </div>
            </div>
          </section>

          <section className="applications-section">
            <h2>Business Applications</h2>
            <div className="applications-grid">
              <div className="application-item">
                <h3>Customer Feedback Analysis</h3>
                <p>Understand customer sentiment and identify key issues at scale</p>
              </div>
              <div className="application-item">
                <h3>Support Ticket Triage</h3>
                <p>Automatically categorize and prioritize support requests</p>
              </div>
              <div className="application-item">
                <h3>Document Processing</h3>
                <p>Extract key information from contracts, reports, and forms</p>
              </div>
              <div className="application-item">
                <h3>Compliance Monitoring</h3>
                <p>Identify potential compliance issues in communications</p>
              </div>
            </div>
          </section>

          <section className="inquiry-section">
            <h2>Unlock the Value in Your Text Data</h2>
            <p>Tell us about your business challenges, and we'll explore how NLP can help you extract insights from your unstructured text.</p>
            
            <form className="inquiry-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <input 
                  type="text" 
                  placeholder="Full Name *" 
                  value={contactInfo.name}
                  onChange={(e) => handleContactInfoChange('name', e.target.value)}
                  className="form-input"
                  required
                />
                <input 
                  type="email" 
                  placeholder="Email Address *" 
                  value={contactInfo.email}
                  onChange={(e) => handleContactInfoChange('email', e.target.value)}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-row">
                <input 
                  type="text" 
                  placeholder="Company (Optional)" 
                  value={contactInfo.company}
                  onChange={(e) => handleContactInfoChange('company', e.target.value)}
                  className="form-input"
                />
                <input 
                  type="tel" 
                  placeholder="Phone (Optional)" 
                  value={contactInfo.phone}
                  onChange={(e) => handleContactInfoChange('phone', e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <select
                  value={contactInfo.nlpType}
                  onChange={(e) => handleContactInfoChange('nlpType', e.target.value)}
                  className="form-select"
                >
                  <option value="sentiment">Sentiment Analysis</option>
                  <option value="classification">Text Classification</option>
                  <option value="ner">Named Entity Recognition</option>
                  <option value="summarization">Text Summarization</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <textarea
                placeholder="Describe your text data and what insights you'd like to extract *"
                value={contactInfo.useCase}
                onChange={(e) => handleContactInfoChange('useCase', e.target.value)}
                className="form-textarea"
                rows="5"
                required
              ></textarea>
              <button type="submit" className="submit-btn">
                Request Consultation
              </button>
              {submitStatus && <p className="submit-status">{submitStatus}</p>}
            </form>
            
            <div className="contact-alternative">
              <p>Prefer to discuss your needs directly?</p>
              <a href="tel:6309408837" className="contact-btn">Call (630) 940-8837</a>
              <p>or email us at <a href="mailto:info@relufox.ai">info@relufox.ai</a></p>
            </div>
          </section>

          <section className="process-section">
            <h2>Our Development Process</h2>
            <div className="process-steps">
              <div className="step">
                <div className="step-number">1</div>
                <h3>Data Assessment</h3>
                <p>Evaluating your text data and defining project requirements</p>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <h3>Model Development</h3>
                <p>Building and training NLP models for your specific needs</p>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <h3>Integration</h3>
                <p>Implementing the solution into your existing systems and workflows</p>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <h3>Deployment & Optimization</h3>
                <p>Launching the solution and continuously improving performance</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default NLP;